<template>
  <div>
    <div v-if="$slots.label" class="tw-mb-2 tw-text-sm tw-font-medium">
      <slot name="label" />
    </div>
    <div
      :class="
        mergeClasses('tw-flex tw-flex-wrap tw-items-center', wrapperClasses)
      "
    >
      <button
        v-for="option in options"
        :key="option.value.toString()"
        :test_id="option.label"
        :class="
          mergeClasses(
            'tw-m-1 tw-flex tw-h-full tw-cursor-pointer tw-items-center tw-justify-between tw-space-x-2 tw-rounded-xl tw-border-2 tw-p-4 tw-shadow-sm focus:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-neutral-200 focus-visible:tw-ring-offset-2',
            themeColors[themeColor].hover,
            isSelected(option)
              ? themeColors[themeColor].selected
              : themeColors[themeColor].unselected,
            buttonClasses,
          )
        "
        @click="setSelectedOption(option)"
      >
        <span>
          <slot :option="option" />
        </span>
        <CheckFullIcon
          v-if="!hideCheckIcon"
          class="tw-order-1 tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0"
          :class="[
            themeColors[themeColor].checkIcon,
            { 'tw-invisible': !isSelected(option) },
          ]"
        />
      </button>
    </div>
    <FormErrors v-if="hasErrors" :errors="errors" class="tw-mt-2" />
  </div>
</template>

<script setup lang="ts" generic="T">
import FormErrors from './FormErrors.vue'
import CheckFullIcon from '@/assets/images/icons/check-full.svg'
import { computed } from 'vue'
import { mergeClasses } from '@/shared/utils/mergeClasses.js'
import { FormOptionType } from '@/shared/types'

const props = withDefaults(
  defineProps<{
    options: (T extends FormOptionType ? T : never)[]
    modelValue?: string | number | null
    errors?: string[] | string
    buttonClasses?: string | string[]
    wrapperClasses?: string | string[]
    hideCheckIcon?: boolean
    themeColor?: 'primary' | 'secondary'
  }>(),
  {
    modelValue: null,
    errors: '',
    buttonClasses: '',
    wrapperClasses: '',
    themeColor: 'secondary',
    hideCheckIcon: false,
  },
)

const emit = defineEmits(['update:modelValue', 'input'])

const hasErrors = computed(() => {
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})

const isSelected = (option: FormOptionType) => option.value === props.modelValue

const setSelectedOption = (option: FormOptionType) => {
  if (isSelected(option)) return
  emit('update:modelValue', option.value)
  // 'input' emit is backward compatibility with Vue 2 v-model
  emit('input', option.value)
}

const themeColors = {
  primary: {
    selected: 'tw-border-primary-500',
    unselected: 'tw-border-neutral-50',
    hover: 'hover:tw-border-primary-500',
    checkIcon: 'tw-text-primary-500',
  },
  secondary: {
    selected: 'tw-border-secondary-500',
    unselected: 'tw-border-secondary-50',
    hover: 'hover:tw-border-secondary-500',
    checkIcon: 'tw-text-secondary-500',
  },
}
</script>
